import { Box, Typography, Button, Card, CardContent, Chip } from "@mui/material";
import Header from "../components/header";
import { Link } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SpeedIcon from "@mui/icons-material/Speed";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";

const SuperSimpleScrum = () => {
  const features = [
    { icon: <ViewKanbanIcon />, title: "Stories & Tasks", description: "Todo, Doing, Done. That's it." },
    { icon: <SpeedIcon />, title: "Built for Speed", description: "No story points. No custom workflows." },
    { icon: <RocketLaunchIcon />, title: "Zero Config", description: "Start executing in seconds, not hours." },
  ];

  const testimonials = [
    { quote: "This feels like using a whiteboard—but better.", author: "Developer" },
    { quote: "I stopped procrastinating because there's nothing to configure.", author: "Solo Founder" },
  ];

  const pricingTiers = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      features: ["1 project", "Unlimited stories", "Unlimited tasks", "Core scrum board"],
      cta: "Start Free",
      highlighted: false,
    },
    {
      name: "Pro",
      price: "$6",
      period: "/month",
      features: ["Unlimited projects", "Everything in Free", "Priority support", "Early access to features"],
      cta: "Go Pro",
      highlighted: true,
    },
  ];

  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
      <Header />
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
          px: 3,
        }}
      >
        <Box sx={{ maxWidth: "1200px", margin: "0 auto", mb: 3 }}>
          <Typography
            component={Link}
            to="/projects"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: "white",
              textDecoration: "none",
              fontFamily: "Roboto",
              fontWeight: 500,
              opacity: 0.85,
              "&:hover": { opacity: 1 },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: "1rem" }} />
            Projects
          </Typography>
        </Box>
        <Box
          sx={{
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
            <Box
              component="img"
              src="/sss_icon.ico"
              alt="SuperSimpleScrum Icon"
              sx={{
                width: 120,
                height: 120,
                borderRadius: 3,
                margin: { xs: "0 auto 24px", md: "0 0 24px 0" },
                boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
              }}
            />

            <Typography
              variant="h2"
              sx={{
                fontFamily: "Roboto Slab",
                fontWeight: 700,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                mb: 2,
              }}
            >
              SuperSimpleScrum
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontFamily: "Roboto",
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                mb: 1,
                opacity: 0.95,
              }}
            >
              Scrum. Minus the bureaucracy.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontSize: { xs: "1rem", md: "1.1rem" },
                mb: 4,
                opacity: 0.9,
              }}
            >
              Built for momentum, not meetings. A scrum board for developers who want to execute fast without the overhead.
            </Typography>

            <Box sx={{ display: "flex", gap: 2, justifyContent: { xs: "center", md: "flex-start" }, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                href="https://www.supersimplescrum.com"
                target="_blank"
                sx={{
                  backgroundColor: "white",
                  color: "#10b981",
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Try SuperSimpleScrum Free
              </Button>
              <Chip
                label="Free forever • No credit card"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 500,
                  alignSelf: "center",
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 4,
                mt: 5,
                justifyContent: { xs: "center", md: "flex-start" },
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: "Roboto Slab" }}>
                  1000+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Projects Created
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: "Roboto Slab" }}>
                  5000+
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Stories Shipped
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: "Roboto Slab" }}>
                  &lt;15s
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Average Setup Time
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, width: "100%" }}>
            <Box
              component="img"
              src="/sss_screenshot.png"
              alt="SuperSimpleScrum Screenshot"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 3,
                boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                border: "2px solid rgba(255,255,255,0.2)",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "white" }}>
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Typography
            variant="h3"
            sx={{ fontFamily: "Roboto Slab", textAlign: "center", mb: 2, fontWeight: 700 }}
          >
            Everything You Need. Nothing You Don't.
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "#666", mb: 6, fontSize: "1.1rem" }}
          >
            No dashboards. No custom workflows. Just pure execution.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 4,
            }}
          >
            {features.map((feature, index) => (
              <Card
                key={index}
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                }}
              >
                <CardContent sx={{ textAlign: "center", p: 4 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      p: 2,
                      borderRadius: "50%",
                      backgroundColor: "#ecfdf5",
                      color: "#10b981",
                      fontSize: "2.5rem",
                      mb: 2,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontFamily: "Roboto Slab", fontWeight: 600, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box sx={{ mt: 8 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 3,
              }}
            >
              {[
                "Backlog, Active, and Complete tabs",
                "Drag-and-drop task management",
                "Clean, distraction-free interface",
                "Coming soon: Teams",
              ].map((item, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CheckCircleIcon sx={{ color: "#10b981", fontSize: "1.5rem" }} />
                  <Typography variant="body1" sx={{ fontFamily: "Roboto" }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "#f5f7fa" }}>
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Typography
            variant="h3"
            sx={{ fontFamily: "Roboto Slab", textAlign: "center", mb: 6, fontWeight: 700 }}
          >
            What Developers Are Saying
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 4,
            }}
          >
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                sx={{ borderRadius: 3, boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)", backgroundColor: "#f9fafb" }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontFamily: "Roboto", fontStyle: "italic", mb: 2, color: "#333", lineHeight: 1.6 }}
                  >
                    "{testimonial.quote}"
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#10b981", fontWeight: 600 }}>
                    — {testimonial.author}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Pricing Section */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "white" }}>
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Typography
            variant="h3"
            sx={{ fontFamily: "Roboto Slab", textAlign: "center", mb: 2, fontWeight: 700 }}
          >
            Simple Pricing
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "#666", mb: 6, fontSize: "1.1rem" }}
          >
            Start free, upgrade when you need more projects
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 4,
              maxWidth: "900px",
              margin: "0 auto",
            }}
          >
            {pricingTiers.map((tier, index) => (
              <Card
                key={index}
                sx={{
                  borderRadius: 3,
                  boxShadow: tier.highlighted
                    ? "0 12px 24px rgba(102, 126, 234, 0.3)"
                    : "0 4px 12px rgba(0, 0, 0, 0.08)",
                  border: tier.highlighted ? "2px solid #10b981" : "none",
                  transform: tier.highlighted ? "scale(1.05)" : "none",
                  transition: "transform 0.3s ease",
                }}
              >
                <CardContent sx={{ p: 4, textAlign: "center" }}>
                  {tier.highlighted && (
                    <Chip
                      label="Most Popular"
                      sx={{ backgroundColor: "#10b981", color: "white", fontWeight: 600, mb: 2 }}
                    />
                  )}
                  <Typography variant="h5" sx={{ fontFamily: "Roboto Slab", fontWeight: 700, mb: 1 }}>
                    {tier.name}
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h3"
                      component="span"
                      sx={{ fontFamily: "Roboto Slab", fontWeight: 700, color: "#10b981" }}
                    >
                      {tier.price}
                    </Typography>
                    <Typography variant="body1" component="span" sx={{ color: "#666", ml: 1 }}>
                      {tier.period}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3, textAlign: "left" }}>
                    {tier.features.map((feature, fIndex) => (
                      <Box key={fIndex} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <CheckCircleIcon sx={{ color: "#10b981", fontSize: "1.2rem" }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    variant={tier.highlighted ? "contained" : "outlined"}
                    fullWidth
                    size="large"
                    href="https://www.supersimplescrum.com"
                    target="_blank"
                    sx={{
                      backgroundColor: tier.highlighted ? "#10b981" : "transparent",
                      color: tier.highlighted ? "white" : "#10b981",
                      borderColor: "#10b981",
                      fontWeight: 600,
                      py: 1.5,
                      "&:hover": {
                        backgroundColor: tier.highlighted ? "#047857" : "#ecfdf5",
                        borderColor: "#047857",
                      },
                    }}
                  >
                    {tier.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Footer CTA */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "#f5f7fa", textAlign: "center" }}>
        <Box sx={{ maxWidth: "800px", margin: "0 auto" }}>
          <Typography variant="h3" sx={{ fontFamily: "Roboto Slab", fontWeight: 700, mb: 3 }}>
            Ready to Ship Faster?
          </Typography>
          <Typography variant="body1" sx={{ color: "#666", mb: 4, fontSize: "1.1rem" }}>
            Join developers who've ditched the overhead and started executing.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://www.supersimplescrum.com"
            target="_blank"
            sx={{
              backgroundColor: "#10b981",
              color: "white",
              fontWeight: 600,
              px: 5,
              py: 2,
              fontSize: "1.1rem",
              mb: 3,
              "&:hover": {
                backgroundColor: "#047857",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 16px rgba(102, 126, 234, 0.3)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Get Started Free
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SuperSimpleScrum;
